import React, { useState, useEffect, useCallback, useRef } from 'react';
import API from '../api/api';
import CreateLoadModal from './CreateLoadModal';
import Pagination from './Pagination';

function LoadBoard() {
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineReason, setShowDeclineReason] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [userOrganization, setUserOrganization] = useState(null);
  
  // Ref for bid input to maintain focus
  const bidInputRef = useRef(null);
  
  // Ref for modal content to maintain scroll position
  const modalContentRef = useRef(null);
  
  // Broadcast tender bid amount
  const [bidAmount, setBidAmount] = useState('');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [itemsPerPage] = useState(20);
  
  // Tick for response deadline countdowns
  const [now, setNow] = useState(() => Date.now());

  // EDI file history for selected load
  const [ediHistory, setEdiHistory] = useState([]);
  const [ediHistoryLoading, setEdiHistoryLoading] = useState(false);
  const [expandedEdiEntry, setExpandedEdiEntry] = useState(null);

  const fetchUserOrganization = useCallback(async () => {
    try {
      // Use the logged-in user's organization from auth/user (not the first org from the list)
      const response = await API.get('auth/user/');
      const user = response.data;
      if (user && (user.organization || user.organization_name)) {
        setUserOrganization({
          id: user.organization,
          name: user.organization_name || ''
        });
      }
    } catch (err) {
      console.error('Error fetching user organization:', err);
    }
  }, []);

  const fetchLoads = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      
      const response = await API.get(`loads/?${params.toString()}`);
      
      // Handle paginated response
      if (response.data.results) {
        // Paginated response
        setLoads(response.data.results);
        setTotalCount(response.data.count || 0);
        setTotalPages(Math.ceil((response.data.count || 0) / itemsPerPage));
      } else {
        // Non-paginated response (fallback)
        const loadsList = Array.isArray(response.data) ? response.data : [];
        setLoads(loadsList);
        setTotalCount(loadsList.length);
        setTotalPages(1);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching loads:', err);
      setError('Failed to load loads. Please try again later.');
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    fetchLoads();
    fetchUserOrganization();
  }, [currentPage, fetchLoads, fetchUserOrganization]);

  // Fetch EDI file history whenever the selected load changes
  const fetchEdiHistory = useCallback(async (loadId) => {
    if (!loadId) return;
    setEdiHistoryLoading(true);
    setExpandedEdiEntry(null);
    try {
      const res = await API.get(`loads/${loadId}/edi-history/`);
      setEdiHistory(res.data.history || []);
    } catch (err) {
      console.error('Error fetching EDI history:', err);
      setEdiHistory([]);
    } finally {
      setEdiHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedLoad?.id) {
      fetchEdiHistory(selectedLoad.id);
    } else {
      setEdiHistory([]);
    }
  }, [selectedLoad?.id, fetchEdiHistory]);

  // Update countdown timers (keep it slow + pause while modal open to avoid scroll jump)
  useEffect(() => {
    if (showModal) return;
    const id = setInterval(() => setNow(Date.now()), 15000); // 15s cadence is plenty for deadlines
    return () => clearInterval(id);
  }, [showModal]);

  // Maintain focus on bid input when bidAmount changes
  useEffect(() => {
    if (showModal && bidAmount !== '' && bidInputRef.current && document.activeElement !== bidInputRef.current) {
      // Save scroll position before refocusing
      const scrollPos = modalContentRef.current?.scrollTop || 0;
      
      // Only refocus if the input exists and is not already focused
      const shouldFocus = selectedLoad?.tender_type === 'broadcast' && selectedLoad?.broadcast_status !== 'bid_submitted';
      if (shouldFocus) {
        bidInputRef.current.focus();
        
        // Restore scroll position after focus
        if (modalContentRef.current) {
          modalContentRef.current.scrollTop = scrollPos;
        }
      }
    }
  }, [bidAmount, showModal, selectedLoad]);


  const handleTenderResponse = (pro_number, response) => {
    setLoading(true);
    // Convert response to boolean: 'A' = true (accepted), 'D' = false (declined)
    const accepted = response === 'A';
    
    const requestData = { accepted };
    if (!accepted && declineReason) {
      requestData.decline_reason = declineReason;
    }
    // Include bid amount for broadcast tenders
    if (accepted && selectedLoad?.tender_type === 'broadcast' && bidAmount) {
      requestData.bid_amount = bidAmount;
    }
    
    API.post(`loads/${pro_number}/respond/`, requestData)
      .then(response => {
        const { message, edi_connection_used, shipment_id, broadcast_status } = response.data;

        if (!accepted) {
          // Immediately remove the rejected load from the list without waiting for a re-fetch
          setLoads(prev => prev.filter(l => l.pro_number !== pro_number));
          setSelectedLoad(null);
        } else {
          // Accepted — re-fetch to reflect the new shipment / status changes
          fetchLoads();
        }

        let alertMsg = message;
        if (edi_connection_used) alertMsg += `\n\nEDI 990 sent via: ${edi_connection_used}`;
        if (shipment_id)        alertMsg += `\n\nShipment created (ID: ${shipment_id})`;
        if (broadcast_status)   alertMsg += `\n\nBroadcast Status: ${broadcast_status}`;
        alert(alertMsg);

        setShowModal(false);
        setShowDeclineReason(false);
        setDeclineReason('');
        setBidAmount('');
      })
      .catch(error => {
        console.error("Error processing tender response:", error);
        const errorMessage = error.response?.data?.error || "Failed to process tender response. Please try again.";
        alert(errorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleApiTenderResponse = (loadId, response) => {
    setLoading(true);
    const accepted = response === 'A';
    
    const requestData = { accepted };
    if (!accepted && declineReason) {
      requestData.decline_reason = declineReason;
    }
    
    API.post(`loads/${loadId}/respond-api/`, requestData)
      .then(response => {
        const { message } = response.data;

        if (!accepted) {
          setLoads(prev => prev.filter(l => l.id !== loadId));
          setSelectedLoad(null);
        } else {
          fetchLoads();
        }

        alert(message);
        setShowModal(false);
        setShowDeclineReason(false);
        setDeclineReason('');
      })
      .catch(error => {
        console.error("Error processing API tender response:", error);
        const errorMessage = error.response?.data?.error || "Failed to process tender response. Please try again.";
        alert(errorMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const openTenderModal = (load) => {
    // If stops aren't present on the list payload, fetch full load detail so we can
    // show origin/destination and all stops before responding.
    const hasStops = Array.isArray(load?.stops) && load.stops.length > 0;
    if (!hasStops) {
      API.get(`loads/${load.id}/`)
        .then((resp) => {
          setSelectedLoad(resp.data || load);
          setShowModal(true);
          setShowDeclineReason(false);
          setDeclineReason('');
          setBidAmount('');
        })
        .catch(() => {
          setSelectedLoad(load);
          setShowModal(true);
          setShowDeclineReason(false);
          setDeclineReason('');
          setBidAmount('');
        });
      return;
    }

    setSelectedLoad(load);
    setShowModal(true);
    setShowDeclineReason(false);
    setDeclineReason('');
    setBidAmount('');
  };

  const openApiTenderModal = (load) => {
    const hasStops = Array.isArray(load?.stops) && load.stops.length > 0;
    if (!hasStops) {
      API.get(`loads/${load.id}/`)
        .then((resp) => {
          setSelectedLoad(resp.data || load);
          setShowModal(true);
          setShowDeclineReason(false);
          setDeclineReason('');
          setBidAmount('');
        })
        .catch(() => {
          setSelectedLoad(load);
          setShowModal(true);
          setShowDeclineReason(false);
          setDeclineReason('');
          setBidAmount('');
        });
      return;
    }

    setSelectedLoad(load);
    setShowModal(true);
    setShowDeclineReason(false);
    setDeclineReason('');
    setBidAmount('');
  };
  
  const getResponseCountdown = (deadline) => {
    if (!deadline) return null;
    const deadlineMs = new Date(deadline).getTime();
    if (!Number.isFinite(deadlineMs)) return null;
    
    const diffMs = deadlineMs - now;
    if (diffMs <= 0) {
      return { text: 'Expired', isExpired: true, isUrgent: true };
    }
    
    const totalMinutes = Math.ceil(diffMs / (1000 * 60));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    const text = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
    
    return {
      text,
      isExpired: false,
      isUrgent: diffMs <= 30 * 60 * 1000, // <= 30 minutes
    };
  };

  const handleMarkChangesReviewed = async (proNumber) => {
    try {
      setLoading(true);
      await API.post(`loads/${proNumber}/mark-changes-reviewed/`);
      alert('Changes marked as reviewed');
      fetchLoads();
      setShowModal(false);
    } catch (error) {
      console.error('Error marking changes as reviewed:', error);
      alert(error.response?.data?.error || 'Failed to mark changes as reviewed');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to check if user can view a load
  // API returns organization/carrier as pk (number) or sometimes nested { id, name }
  const getOrgId = (field) => {
    if (field == null) return null;
    return typeof field === 'object' && field !== null && 'id' in field ? field.id : field;
  };
  const canViewLoad = (load) => {
    if (!userOrganization || userOrganization.id == null) return false;
    
    // Admin can see all loads
    if (userOrganization.name === 'Admin') return true;
    
    const loadOrgId = getOrgId(load.organization);
    const loadCarrierId = getOrgId(load.carrier);
    const userOrgId = userOrganization.id;
    // Regular users can see loads where their org is owner, carrier, or load has no carrier
    return (
      (loadOrgId != null && loadOrgId === userOrgId) ||
      (loadCarrierId != null && loadCarrierId === userOrgId) ||
      (loadCarrierId == null && load.carrier == null)  // No carrier = visible to all
    );
  };

  // Separate loads by type (with proper permission filtering)
  const ediTenders = loads.filter(load => 
    load.created_via_edi === true &&
    load.status !== 'booked' &&
    load.status !== 'rejected' &&
    load.tender_status !== 'rejected' &&
    canViewLoad(load)
  );
  
  // API tenders: loads created via API that are targeted at specific shippers (have both shipper and carrier specified)
  // Only show to users who are either the carrier, shipper, or admin
  const apiTenders = loads.filter(load => 
    load.created_via_edi === false && 
    load.status === 'available' &&
    load.shipper && 
    load.carrier &&
    canViewLoad(load)
  );
  
  // Load board: loads without carriers (available for any carrier to book) or admin can see all
  const loadBoardLoads = loads.filter(load => 
    load.created_via_edi === false && 
    load.status === 'available' && // Show available loads (marketplace)
    canViewLoad(load) &&
    (!load.carrier || userOrganization.name === 'Admin') // No carrier assigned OR admin can see all
  );

  // All organizations are treated as carriers now
  const canCreateLoads = !!userOrganization;
  const canSeeTenders = !!userOrganization;

  const handleLoadCreated = (newLoad) => {
    console.log('Load created successfully:', newLoad);
    
    // Add the new load to the current loads state immediately for better UX
    setLoads(prevLoads => [newLoad, ...prevLoads]);
    
    // Also refresh the loads list to ensure we have the latest data
    fetchLoads();
    
    // Show success message
    alert(`Load created successfully! PRO #${newLoad.pro_number}`);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const TenderResponseModal = () => {
    if (!selectedLoad || !showModal) return null;

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-start justify-center overflow-hidden h-full w-full z-50" onClick={(e) => {
        // Prevent clicks inside the modal from closing it
        if (e.target === e.currentTarget) {
          setShowModal(false);
        }
      }}>
        <div ref={modalContentRef} className="relative mt-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white max-h-[calc(100vh-10rem)] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="mt-3">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedLoad.created_via_edi ? 'EDI Tender Response' : 'API Tender Response'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Decline Reason Section */}
            {showDeclineReason && (
              <div className="mb-6">
                <h6 className="text-sm font-semibold text-gray-700 mb-3">Decline Reason</h6>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm text-red-700 mb-3">
                    Please select a reason for declining this tender:
                  </p>
                  <select
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  >
                    <option value="">Select a reason...</option>
                    <option value="T06">No capacity available</option>
                    <option value="T05">Trailer class not available</option>
                    <option value="W15">Wrong equipment type</option>
                    <option value="WLT">Weight limit exceeded</option>
                    <option value="A81">Not avail on requested date</option>
                    <option value="RUN">Reason unknown</option>
                    <option value="ZZZ">Other (mutually defined)</option>
                    <option value="015">Default / General decline</option>
                  </select>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div>
                <h6 className="text-sm font-semibold text-gray-700 mb-3">Load Details</h6>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">Customer PO:</span>
                      <span className="text-gray-900">{selectedLoad.cust_po_number || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">RFQ #:</span>
                      <span className="text-gray-900">{selectedLoad.rfq_number || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">Response deadline:</span>
                      <span className="text-gray-900">
                        {selectedLoad.response_deadline ? new Date(selectedLoad.response_deadline).toLocaleString() : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">PO Number:</span>
                      <span className="text-gray-900">{selectedLoad.po_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">PRO Number:</span>
                      <span className="text-gray-900">{selectedLoad.pro_number}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">Carrier Code:</span>
                      <span className="text-gray-900">{selectedLoad.carrier_code || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">Shipper Code:</span>
                      <span className="text-gray-900">{selectedLoad.shipper_name || selectedLoad.shipper_code || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">Equipment:</span>
                      <span className="text-gray-900">{selectedLoad.equipment_type}</span>
                    </div>
                    {selectedLoad.temperature_mode && (
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Temp Mode:</span>
                        <span className="text-gray-900 capitalize">{selectedLoad.temperature_mode}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">Weight:</span>
                      <span className="text-gray-900">{selectedLoad.weight} lbs</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">Total Rate:</span>
                      <span className="text-gray-900 font-semibold">${selectedLoad.rate}</span>
                    </div>
                  </div>

                  {/* Rate line items breakdown */}
                  {selectedLoad.rate_line_items && selectedLoad.rate_line_items.length > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Rate Breakdown</p>
                      <div className="rounded-lg border border-gray-200 overflow-hidden">
                        <table className="min-w-full divide-y divide-gray-200 text-xs">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left font-medium text-gray-500 uppercase tracking-wide">Description</th>
                              <th className="px-3 py-2 text-right font-medium text-gray-500 uppercase tracking-wide">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {selectedLoad.rate_line_items.map((item, i) => (
                              <tr key={i} className="hover:bg-gray-50">
                                <td className="px-3 py-2 text-gray-700">{item.description || '—'}</td>
                                <td className="px-3 py-2 text-right font-medium text-gray-900">
                                  ${Number(item.amount).toFixed(2)}
                                </td>
                              </tr>
                            ))}
                            <tr className="bg-gray-50 font-semibold">
                              <td className="px-3 py-2 text-gray-800">Total</td>
                              <td className="px-3 py-2 text-right text-gray-900">${selectedLoad.rate}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div>
                <h6 className="text-sm font-semibold text-gray-700 mb-3">Appointment Times</h6>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">Pickup:</span>
                      <span className="text-gray-900">
                        {selectedLoad.pickup_date && new Date(selectedLoad.pickup_date).toLocaleDateString()}
                        {selectedLoad.pickup_time && selectedLoad.pickup_time !== 'N/A' && (
                          <div className="text-gray-500 text-xs">
                            {selectedLoad.pickup_time}
                          </div>
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">Delivery:</span>
                      <span className="text-gray-900">
                        {selectedLoad.delivery_date && new Date(selectedLoad.delivery_date).toLocaleDateString()}
                        {selectedLoad.dropoff_time && selectedLoad.dropoff_time !== 'N/A' && (
                          <div className="text-gray-500 text-xs">
                            {selectedLoad.dropoff_time}
                          </div>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h6 className="text-sm font-semibold text-gray-700">
                  {selectedLoad.stops && selectedLoad.stops.length > 2 ? `Route (${selectedLoad.stops.length} stops)` : 'Pickup & Delivery'}
                </h6>
                {selectedLoad.stop_count > 2 && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                    Multi-Stop
                  </span>
                )}
              </div>
              
              {/* Show stops if available */}
              {selectedLoad.stops && selectedLoad.stops.length > 0 ? (
                <div className="space-y-3">
                  {selectedLoad.stops.map((stop, index) => (
                    <div 
                      key={stop.id || index} 
                      className={`rounded-lg p-4 ${
                        stop.is_pickup 
                          ? 'bg-green-50 border border-green-200' 
                          : 'bg-primary-50 border border-primary-200'
                      }`}
                    >
                      <div className="flex items-start">
                        <div className="flex-shrink-0">
                          <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold ${
                            stop.is_pickup ? 'bg-green-600 text-white' : 'bg-primary-600 text-white'
                          }`}>
                            {stop.sequence}
                          </span>
                        </div>
                        <div className="ml-3 flex-1">
                          <div className="flex items-center gap-2">
                            <h6 className="text-sm font-semibold text-gray-900">
                              {stop.stop_type_display || (stop.is_pickup ? 'Pickup' : 'Delivery')}
                            </h6>
                          </div>
                          <div className="mt-1 text-sm text-gray-700">
                            <div className="font-medium">{stop.name || 'N/A'}</div>
                            <div className="text-gray-600 text-xs mt-1">
                              {stop.full_address || stop.address || 'N/A'}
                            </div>
                            <div className="text-gray-600 text-xs mt-2">
                              <strong>Date:</strong> {stop.appointment_date ? new Date(stop.appointment_date).toLocaleDateString() : 'N/A'}
                              {stop.appointment_time && (
                                <span className="ml-2">
                                  <strong>Time:</strong> {stop.appointment_time}
                                </span>
                              )}
                            </div>
                            {stop.contact_email && (
                              <div className="text-gray-600 text-xs mt-1">
                                <strong>Contact:</strong> <a href={`mailto:${stop.contact_email}`} className="text-blue-600 hover:underline">{stop.contact_email}</a>
                              </div>
                            )}
                            {/* Order Line Items from OID+LAD segments */}
                            {stop.order_line_items && stop.order_line_items.length > 0 && (
                              <div className="mt-3">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Order Items</p>
                                <div className="rounded border border-gray-200 overflow-hidden">
                                  <table className="min-w-full text-xs divide-y divide-gray-200">
                                    <thead className="bg-gray-100">
                                      <tr>
                                        <th className="px-2 py-1 text-left font-medium text-gray-500 uppercase tracking-wide">Order #</th>
                                        <th className="px-2 py-1 text-left font-medium text-gray-500 uppercase tracking-wide">Product</th>
                                        <th className="px-2 py-1 text-right font-medium text-gray-500 uppercase tracking-wide">Qty</th>
                                        <th className="px-2 py-1 text-right font-medium text-gray-500 uppercase tracking-wide">Weight</th>
                                        <th className="px-2 py-1 text-left font-medium text-gray-500 uppercase tracking-wide">Mfr</th>
                                      </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-100">
                                      {stop.order_line_items.map((item, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50">
                                          <td className="px-2 py-1 font-mono text-gray-700">{item.order_number || '—'}</td>
                                          <td className="px-2 py-1 text-gray-800">
                                            <div>{item.description || '—'}</div>
                                            {item.product_id && <div className="text-gray-400">ID: {item.product_id}</div>}
                                          </td>
                                          <td className="px-2 py-1 text-right text-gray-700">
                                            {item.quantity != null ? `${item.quantity} ${item.package_type || ''}` : '—'}
                                          </td>
                                          <td className="px-2 py-1 text-right text-gray-700">
                                            {item.weight != null ? `${Number(item.weight).toLocaleString()} lbs` : '—'}
                                          </td>
                                          <td className="px-2 py-1 text-gray-600">{item.manufacturer_name || '—'}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Fallback to legacy origin/destination display */
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Pickup Information */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h6 className="text-sm font-semibold text-gray-900">Pickup</h6>
                        <div className="mt-1 text-sm text-gray-700">
                          <div className="font-medium">{selectedLoad.origin_name || 'N/A'}</div>
                          <div className="text-gray-600 text-xs mt-1">{selectedLoad.origin_address || 'N/A'}</div>
                          <div className="text-gray-600 text-xs mt-2">
                            <strong>Date:</strong> {new Date(selectedLoad.pickup_date).toLocaleDateString()}
                            {selectedLoad.pickup_time && (
                              <span className="ml-2">
                                <strong>Time:</strong> {selectedLoad.pickup_time}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Delivery Information */}
                  <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-primary-600" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
                          <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />
                        </svg>
                      </div>
                      <div className="ml-3 flex-1">
                        <h6 className="text-sm font-semibold text-gray-900">Delivery</h6>
                        <div className="mt-1 text-sm text-gray-700">
                          <div className="font-medium">{selectedLoad.destination_name || 'N/A'}</div>
                          <div className="text-gray-600 text-xs mt-1">{selectedLoad.destination_address || 'N/A'}</div>
                          <div className="text-gray-600 text-xs mt-2">
                            <strong>Date:</strong> {new Date(selectedLoad.delivery_date).toLocaleDateString()}
                            {selectedLoad.delivery_time && (
                              <span className="ml-2">
                                <strong>Time:</strong> {selectedLoad.delivery_time}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Pickup Appointment (OpenDock automation) */}
            <div className="mb-6">
              <h6 className="text-sm font-semibold text-gray-700 mb-3">Pickup Appointment</h6>
              {(selectedLoad.dock_appointments || []).length > 0 ? (
                <div className="space-y-2">
                  {selectedLoad.dock_appointments.map((appt) => (
                    <div
                      key={appt.id}
                      className={`rounded-lg border p-3 text-sm ${
                        appt.status === 'booked'
                          ? 'bg-green-50 border-green-200'
                          : appt.status === 'failed'
                          ? 'bg-red-50 border-red-200'
                          : 'bg-yellow-50 border-yellow-200'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">
                          {(appt.platform_display || appt.platform)} — {appt.direction === 'pickup' ? 'Pickup' : 'Delivery'}
                        </span>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            appt.status === 'booked'
                              ? 'bg-green-100 text-green-800'
                              : appt.status === 'failed'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {appt.status_display || appt.status}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                        {appt.warehouse_name && <div><strong>Warehouse:</strong> {appt.warehouse_name}</div>}
                        {appt.appointment_start && (
                          <div><strong>Appointment:</strong> {new Date(appt.appointment_start).toLocaleString()}</div>
                        )}
                        {appt.confirmation_number && <div><strong>Confirmation #:</strong> {appt.confirmation_number}</div>}
                        <div><strong>Booked by:</strong> {appt.booked_by}{appt.booked_at ? ` on ${new Date(appt.booked_at).toLocaleString()}` : ''}</div>
                        {appt.error_message && <div className="text-red-600">{appt.error_message}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-500">No dock appointment booked for this load yet.</p>
              )}
              {/* Pickup booking happens after the tender is accepted, from the
                  shipment view — no manual booking button on the load board. */}
            </div>

            {/* EDI Change Notification — strikethrough diff + inline Accept/Decline */}
            {selectedLoad.has_pending_changes && (
              <div className="border-2 border-yellow-400 rounded-lg overflow-hidden mb-6">
                {/* Header */}
                <div className="bg-yellow-400 px-5 py-3 flex items-center gap-3">
                  <svg className="w-5 h-5 text-yellow-900 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <span className="font-bold text-yellow-900">Shipper sent a 204 Change (B2A*04)</span>
                    <span className="ml-3 text-xs text-yellow-800">
                      {selectedLoad.edi_change_count > 1 ? `${selectedLoad.edi_change_count} updates received · ` : ''}
                      Last: {selectedLoad.last_edi_change_at ? new Date(selectedLoad.last_edi_change_at).toLocaleString() : 'N/A'}
                    </span>
                  </div>
                </div>

                {/* Diff body */}
                <div className="bg-yellow-50 px-5 py-4">
                  {(() => {
                    const lc = selectedLoad.latest_change;
                    const fieldChanges = lc?.changed_fields?.length > 0;
                    const stopChanges = lc?.stops_changes?.changes?.length > 0;
                    const STOP_FIELD_LABELS = {
                      order_line_items: 'Order Line Items',
                      contact_email: 'Contact Email',
                    };

                    if (!lc || (!fieldChanges && !stopChanges)) {
                      return (
                        <p className="text-sm text-yellow-800 italic">Change details are being processed and will appear here shortly.</p>
                      );
                    }

                    return (
                      <div className="bg-white rounded-md p-4 border border-yellow-300">
                        <h5 className="text-sm font-semibold text-gray-900 mb-3">Changes Summary:</h5>
                        {fieldChanges && (
                          <div className="space-y-3">
                            {lc.changed_fields.map((field, idx) => {
                              const oldValue = lc.old_values?.[field];
                              const newValue = lc.new_values?.[field];
                              const fieldLabel = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                              return (
                                <div key={idx} className="flex items-start text-sm border-b border-gray-200 pb-2 last:border-0">
                                  <div className="flex-1">
                                    <span className="font-medium text-gray-700">{fieldLabel}:</span>
                                    <div className="mt-1 grid grid-cols-2 gap-2">
                                      <div className="bg-red-50 border border-red-200 rounded px-2 py-1">
                                        <span className="text-xs text-red-600 font-semibold block">Old:</span>
                                        <span className="text-red-800">{oldValue || 'N/A'}</span>
                                      </div>
                                      <div className="bg-green-50 border border-green-200 rounded px-2 py-1">
                                        <span className="text-xs text-green-600 font-semibold block">New:</span>
                                        <span className="text-green-800">{newValue || 'N/A'}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {stopChanges && (
                          <div className={fieldChanges ? 'mt-4 pt-4 border-t border-gray-200' : ''}>
                            <h6 className="text-sm font-semibold text-gray-900 mb-2">Stop Changes:</h6>
                            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                              {lc.stops_changes.changes.map((change, idx) => {
                                if (change.action === 'added') return <li key={idx}>Stop {change.stop_sequence} added: {change.name}</li>;
                                if (change.action === 'removed') return <li key={idx}>Stop {change.stop_sequence} removed: {change.name}</li>;
                                const label = STOP_FIELD_LABELS[change.field] || change.field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                if (change.field === 'order_line_items' && (change.old_items || change.new_items)) {
                                  const formatItem = (item) => [
                                    item.order_number && `PO# ${item.order_number}`,
                                    item.line_item_number && `Line ${item.line_item_number}`,
                                    item.description,
                                    item.quantity && item.package_type && `${item.quantity} ${item.package_type}`,
                                    item.weight && `${item.weight} ${item.weight_unit || 'lbs'}`,
                                  ].filter(Boolean).join(' · ');
                                  return (
                                    <li key={idx}>
                                      <span>Stop {change.stop_sequence} — {label}:</span>
                                      <ul className="ml-4 mt-1 space-y-0.5">
                                        {(change.old_items || []).map((item, i) => <li key={`old-${i}`} className="text-red-600 line-through text-xs">{formatItem(item)}</li>)}
                                        {(change.new_items || []).map((item, i) => <li key={`new-${i}`} className="text-green-700 font-medium text-xs">{formatItem(item)}</li>)}
                                      </ul>
                                    </li>
                                  );
                                }
                                return <li key={idx}>Stop {change.stop_sequence} — {label}: {change.old_value} → {change.new_value}</li>;
                              })}
                            </ul>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Inline Accept / Decline actions */}
                  <div className="mt-4 pt-4 border-t border-yellow-300 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                      onClick={() => {
                        if (window.confirm('Accept these changes? A 990 acceptance will be sent to the shipper.')) {
                          if (selectedLoad.created_via_edi) {
                            handleTenderResponse(selectedLoad.pro_number, 'A');
                          } else {
                            handleApiTenderResponse(selectedLoad.id, 'A');
                          }
                        }
                      }}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      Accept Changes
                    </button>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                      onClick={() => {
                        if (!showDeclineReason) {
                          setShowDeclineReason(true);
                        } else if (declineReason) {
                          if (window.confirm('Decline these changes? A 990 decline will be sent to the shipper.')) {
                            if (selectedLoad.created_via_edi) {
                              handleTenderResponse(selectedLoad.pro_number, 'D');
                            } else {
                              handleApiTenderResponse(selectedLoad.id, 'D');
                            }
                          }
                        } else {
                          alert('Please select a decline reason first.');
                        }
                      }}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      Decline Changes
                    </button>
                    <button
                      type="button"
                      className="text-xs text-yellow-700 underline hover:text-yellow-900"
                      onClick={() => handleMarkChangesReviewed(selectedLoad.pro_number)}
                    >
                      Mark as reviewed (no response)
                    </button>
                    {showDeclineReason && (
                      <div className="w-full mt-2">
                        <select
                          value={declineReason}
                          onChange={(e) => setDeclineReason(e.target.value)}
                          className="block w-full max-w-xs px-3 py-1.5 text-sm border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                        >
                          <option value="">Select a decline reason…</option>
                          <option value="T06">No capacity available</option>
                          <option value="T05">Trailer class not available</option>
                          <option value="W15">Wrong equipment type</option>
                          <option value="WLT">Weight limit exceeded</option>
                          <option value="A81">Not available on requested date</option>
                          <option value="RUN">Reason unknown</option>
                          <option value="ZZZ">Other (mutually defined)</option>
                          <option value="015">Default / General decline</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {/* EDI File History — original 204 tender + all updates/cancellations */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                EDI File History
              </h4>
              {ediHistoryLoading ? (
                <p className="text-sm text-gray-400">Loading…</p>
              ) : ediHistory.length === 0 ? (
                <p className="text-sm text-gray-400">No EDI files recorded for this load.</p>
              ) : (
                <div className="space-y-2">
                  {ediHistory.map((entry) => {
                    const isExpanded = expandedEdiEntry === entry.id;
                    const labelColor = entry.event_type === 'edi.204.received'
                      ? 'bg-blue-100 text-blue-700'
                      : entry.event_type === 'load.edi_change_received'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-red-100 text-red-700';
                    return (
                      <div key={entry.id} className="border border-gray-100 rounded-lg overflow-hidden">
                        <button
                          type="button"
                          onClick={() => setExpandedEdiEntry(isExpanded ? null : entry.id)}
                          className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                        >
                          <div className="flex items-center gap-2">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${labelColor}`}>
                              {entry.label}
                            </span>
                            {entry.changed_fields.length > 0 && (
                              <span className="text-xs text-gray-500">{entry.changed_fields.length} field{entry.changed_fields.length !== 1 ? 's' : ''} changed</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">{new Date(entry.occurred_at).toLocaleString()}</span>
                            <svg className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="px-3 py-2 bg-white">
                            {entry.raw_payload ? (
                              <pre className="text-xs text-gray-700 bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all max-h-64 overflow-y-auto font-mono">
                                {entry.raw_payload}
                              </pre>
                            ) : (
                              <p className="text-xs text-gray-400 italic">Raw file content not available for this event.</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {selectedLoad.created_via_edi && (
              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-primary-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <span className="text-sm text-primary-700">
                    Responding to this tender will generate an EDI 990 response that will be sent to the shipper.
                  </span>
                </div>
              </div>
            )}
            
            {/* Broadcast Bid Amount Section */}
            {selectedLoad.tender_type === 'broadcast' && (
              <div className="mb-6">
                <h6 className="text-sm font-semibold text-gray-700 mb-3">
                  Broadcast Tender Bid
                  {selectedLoad.broadcast_status && (
                    <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                      selectedLoad.broadcast_status === 'bid_submitted' ? 'bg-yellow-100 text-yellow-800' :
                      selectedLoad.broadcast_status === 'awarded' ? 'bg-green-100 text-green-800' :
                      selectedLoad.broadcast_status === 'not_awarded' ? 'bg-gray-100 text-gray-800' :
                      'bg-primary-100 text-primary-800'
                    }`}>
                      {selectedLoad.broadcast_status.replace(/_/g, ' ').toUpperCase()}
                    </span>
                  )}
                </h6>
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                  <p className="text-sm text-primary-700 mb-3">
                    This is a broadcast tender. Enter your bid amount to compete for this load:
                  </p>
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl text-primary-700">$</span>
                    <input
                      key="bid-amount-input"
                      ref={bidInputRef}
                      id="broadcast-bid-input"
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]*\.?[0-9]*"
                      value={bidAmount}
                      onChange={(e) => {
                        // Only allow numbers and decimal point
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          setBidAmount(value);
                        }
                      }}
                      onBlur={(e) => {
                        // Format to 2 decimal places on blur if it's a valid number
                        const value = e.target.value;
                        if (value && !isNaN(parseFloat(value))) {
                          setBidAmount(parseFloat(value).toFixed(2));
                        }
                      }}
                      placeholder="Enter bid amount"
                      className="flex-1 px-3 py-2 border border-primary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      disabled={selectedLoad.broadcast_status === 'bid_submitted'}
                      autoFocus={selectedLoad.tender_type === 'broadcast' && !selectedLoad.broadcast_status}
                    />
                  </div>
                  {selectedLoad.broadcast_status === 'bid_submitted' && selectedLoad.bid_amount && (
                    <p className="text-xs text-primary-600 mt-2">
                      Current bid: ${selectedLoad.bid_amount}
                    </p>
                  )}
                  {selectedLoad.rate && (
                    <p className="text-xs text-primary-600 mt-2">
                      Shipper's posted total rate: ${selectedLoad.rate}
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {/* Bottom action bar — only shown when there are NO pending changes (changes handled inline above) */}
            {!selectedLoad.has_pending_changes && (
              <div className="flex justify-end items-center gap-3">
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  onClick={() => {
                    if (selectedLoad.tender_type === 'broadcast' && !bidAmount) {
                      alert('Please enter a bid amount for this broadcast tender');
                      return;
                    }
                    if (selectedLoad.created_via_edi) {
                      handleTenderResponse(selectedLoad.pro_number, 'A');
                    } else {
                      handleApiTenderResponse(selectedLoad.id, 'A');
                    }
                  }}
                >
                  {selectedLoad.tender_type === 'broadcast' ? 'Submit Bid' : 'Accept Tender'}
                </button>
                <button
                  type="button"
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  onClick={() => {
                    if (!showDeclineReason) {
                      setShowDeclineReason(true);
                    } else if (declineReason) {
                      if (selectedLoad.created_via_edi) {
                        handleTenderResponse(selectedLoad.pro_number, 'D');
                      } else {
                        handleApiTenderResponse(selectedLoad.id, 'D');
                      }
                    } else {
                      alert('Please select a decline reason');
                    }
                  }}
                >
                  {showDeclineReason ? 'Confirm Decline' : 'Decline Tender'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const LoadCardGrid = ({ loads, title, showRespondButton = false, isApiTenders = false, showPagination = false }) => {
    // Sort loads to show available ones at the top
    const sortedLoads = [...loads].sort((a, b) => {
      if (a.status === 'available' && b.status !== 'available') return -1;
      if (a.status !== 'available' && b.status === 'available') return 1;
      return 0;
    });

    return (
      <div className="mb-8">
        <div className="mb-6">
          <h2 className="text-lg leading-6 font-medium text-gray-900">{title}</h2>
          <p className="mt-1 text-sm text-gray-600">
            {showRespondButton && isApiTenders
              ? "API tenders created via the application. Respond to accept or decline."
              : showRespondButton
              ? "EDI tenders received via 204 EDI files. Respond to accept or decline."
              : "Loads available for booking on the load board."
            }
          </p>
        </div>

        {sortedLoads.length === 0 ? (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No loads found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {showRespondButton && isApiTenders
                ? "No API tenders are currently pending response."
                : showRespondButton
                ? "No EDI tenders are currently pending response."
                : "No loads are currently available on the load board."
              }
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 lg:gap-6">
            {sortedLoads.map((load) => (
              <div key={load.id} className={`bg-white rounded-lg shadow-md border-l-4 ${
                load.status === 'available' 
                  ? 'border-l-green-400 bg-green-50' 
                  : load.status === 'booked'
                  ? 'border-l-primary-400 bg-primary-50'
                  : load.status === 'rejected'
                  ? 'border-l-red-400 bg-red-50'
                  : 'border-l-gray-400'
              } hover:shadow-lg transition-shadow duration-200`}>
                <div className="p-4 lg:p-6">
                  {/* Header with PRO Number and Status */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center flex-wrap gap-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        PRO #{load.pro_number || 'N/A'}
                      </h3>
                      {load.status === 'available' && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          New
                        </span>
                      )}
                      {load.has_pending_changes && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          Changes
                        </span>
                      )}
                      {load.edi_cancellation_received && !load.cancellation_acknowledged && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-400 animate-pulse">
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                          CANCELLED
                        </span>
                      )}
                      {load.tender_type === 'broadcast' && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          load.broadcast_status === 'bid_submitted' ? 'bg-yellow-100 text-yellow-800 border border-yellow-300' :
                          load.broadcast_status === 'awarded' ? 'bg-green-100 text-green-800 border border-green-400 animate-pulse' :
                          load.broadcast_status === 'not_awarded' ? 'bg-gray-100 text-gray-700' :
                          'bg-purple-100 text-purple-800 border border-purple-300'
                        }`}>
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                          </svg>
                          {load.broadcast_status === 'bid_submitted' ? 'BID SUBMITTED' :
                           load.broadcast_status === 'awarded' ? 'AWARDED!' :
                           load.broadcast_status === 'not_awarded' ? 'NOT AWARDED' :
                           'BROADCAST'}
                        </span>
                      )}
                      {load.temperature_mode && (
                        <span className="inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700 uppercase tracking-wide">
                          {load.temperature_mode}
                        </span>
                      )}
                    </div>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      load.status === 'available'
                        ? 'bg-green-100 text-green-800'
                        : load.status === 'booked'
                        ? 'bg-primary-100 text-primary-800'
                        : load.status === 'rejected'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {load.status?.toUpperCase() || 'N/A'}
                    </span>
                  </div>

                  {/* Load Details */}
                  <div className="space-y-3 mb-4">
                    {load.created_via_edi && load.tender_status === 'pending' && load.response_deadline && (() => {
                      const cd = getResponseCountdown(load.response_deadline);
                      if (!cd) return null;
                      return (
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-gray-600">Respond in:</span>
                          <span
                            className={`text-sm font-semibold ${
                              cd.isExpired ? 'text-red-700' : cd.isUrgent ? 'text-orange-700' : 'text-gray-900'
                            }`}
                            title={new Date(load.response_deadline).toLocaleString()}
                          >
                            {cd.text}
                          </span>
                        </div>
                      );
                    })()}
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">PO Number:</span>
                      <span className="text-sm text-gray-900">{load.po_number || 'N/A'}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Customer PO:</span>
                      <span className="text-sm text-gray-900">{load.cust_po_number || 'N/A'}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Shipper:</span>
                      <span className="text-sm text-gray-900">{load.shipper_name || load.shipper_code || 'N/A'}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Carrier:</span>
                      <span className="text-sm text-gray-900">{load.carrier_code || 'N/A'}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Equipment:</span>
                      <span className="text-sm text-gray-900">{load.equipment_type || 'N/A'}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Weight:</span>
                      <span className="text-sm text-gray-900">{load.weight ? `${load.weight} lbs` : 'N/A'}</span>
                    </div>

                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-600">Rate:</span>
                      <span className="text-sm text-gray-900">{load.rate ? `$${load.rate}` : 'N/A'}</span>
                    </div>
                  </div>

                  {/* Route Information */}
                  <div className="border-t pt-3 mb-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Route</span>
                        {load.stop_count > 2 && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                            {load.stop_count} stops
                          </span>
                        )}
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Origin</span>
                        <p className="text-sm text-gray-900 truncate" title={load.origin || load.origin_name || ''}>
                          {load.origin_name || load.origin || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Destination</span>
                        <p className="text-sm text-gray-900 truncate" title={load.destination || load.destination_name || ''}>
                          {load.destination_name || load.destination || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Dates */}
                  <div className="border-t pt-3 mb-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Pickup</span>
                        <p className="text-sm text-gray-900">
                          {load.pickup_date ? new Date(load.pickup_date).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Delivery</span>
                        <p className="text-sm text-gray-900">
                          {load.delivery_date ? new Date(load.delivery_date).toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Actions */}
                  {showRespondButton && 
                   load.tender_status === 'pending' && 
                   !load.edi_cancellation_received && 
                   load.broadcast_status !== 'cancelled' && 
                   load.broadcast_status !== 'awarded' && (
                    <div className="border-t pt-3">
                      <button
                        onClick={() => isApiTenders ? openApiTenderModal(load) : openTenderModal(load)}
                        className="w-full bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors duration-200"
                      >
                        Respond
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Pagination */}
        {showPagination && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalCount}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Tenders</h1>
            <p className="mt-2 text-sm text-gray-600">
              Manage EDI tenders, API tenders, and load board availability
            </p>
          </div>
          {canCreateLoads && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              <svg className="-ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Create Load
            </button>
          )}
        </div>
      </div>

      {/* EDI Tenders Section - Only show if user can see tenders */}
      {canSeeTenders && (
        <LoadCardGrid 
          loads={ediTenders} 
          title="EDI Tenders" 
          showRespondButton={true}
        />
      )}

      {/* API Tenders Section - Only show if user can see tenders */}
      {canSeeTenders && (
        <LoadCardGrid 
          loads={apiTenders} 
          title="API Tenders" 
          showRespondButton={true}
          isApiTenders={true}
        />
      )}

      {/* Load Board Section */}
      <LoadCardGrid 
        loads={loadBoardLoads} 
        title="Load Board" 
        showRespondButton={true}
        showPagination={true}
      />
      
      <TenderResponseModal />
      <CreateLoadModal 
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onLoadCreated={handleLoadCreated}
      />
    </div>
  );
}

export default LoadBoard;
